import { LoadingCircle } from "@ui-lib/src"
import {
	getVesselImageCandidateUrls,
	getVesselPlaceholderImageUrl,
} from "@utils/helpers/cdnImagePaths"
import { type ReactElement, useEffect, useRef, useState } from "react"

export type ShipImgContainerProps = {
	shipName: string
	companyName?: string
}

// created by AI: Track which asset identity the current retry index belongs to.
type ImageProbeState = {
	imageKey: string
	candidateIndex: number
}

// created by AI: Track which concrete URL has completed loading or finally failed.
type SettledImageState = {
	imageKey: string
	imageSrc: string
}

// created by AI: Render a vessel image from inferred CDN paths without requiring a
// hardcoded frontend map for every vessel asset.
export default function ShipImgContainer({
	shipName,
	companyName = "Unspecified",
}: ShipImgContainerProps): ReactElement {
	const imageKey = `${companyName}\u0000${shipName}`

	// created by AI: Try the inferred vessel image URLs first, then the existing
	// company placeholder. Vessel names are intentionally not normalized or encoded.
	const imageCandidates = [
		...getVesselImageCandidateUrls(shipName),
		getVesselPlaceholderImageUrl(companyName),
	]
	const [imageProbeState, setImageProbeState] = useState<ImageProbeState>({
		imageKey,
		candidateIndex: 0,
	})
	const candidateIndex =
		imageProbeState.imageKey === imageKey ? imageProbeState.candidateIndex : 0
	const imageSrc = imageCandidates[candidateIndex] ?? getVesselPlaceholderImageUrl(companyName)

	// created by AI: The browser tells us when each concrete image URL has settled.
	// Until the current URL has fired either load or final error, keep loading visible.
	const imageRef = useRef<HTMLImageElement>(null)
	const [settledImageState, setSettledImageState] = useState<SettledImageState>()
	const isImageLoading =
		settledImageState?.imageKey !== imageKey || settledImageState.imageSrc !== imageSrc

	useEffect(() => {
		// created by AI: Cached CDN images can already be complete before React observes
		// a load event, so also read the browser's completed state after each src change.
		if (imageRef.current?.complete === true) {
			setSettledImageState({ imageKey, imageSrc })
		}
	}, [imageKey, imageSrc])

	return (
		<div
			className={`border-gray-eee relative w-full overflow-hidden border-b-1 ${
				isImageLoading ? "bg-dipai-secondary-901/5 aspect-video" : ""
			}`}
		>
			{isImageLoading ? (
				<div className="absolute inset-0 flex items-center justify-center">
					<LoadingCircle size="sm" />
				</div>
			) : null}
			<img
				ref={imageRef}
				className={`w-full object-cover transition-opacity duration-200 ${
					isImageLoading ? "opacity-0" : "opacity-100"
				}`}
				alt={`${shipName} vessel`}
				src={imageSrc}
				onError={() => {
					// created by AI: Advance through jpg/png/placeholder, then settle so a
					// broken placeholder cannot keep the card spinning forever.
					if (candidateIndex < imageCandidates.length - 1) {
						setImageProbeState({
							imageKey,
							candidateIndex: candidateIndex + 1,
						})
					} else {
						setSettledImageState({ imageKey, imageSrc })
					}
				}}
				onLoad={() => setSettledImageState({ imageKey, imageSrc })}
			/>
		</div>
	)
}

// created by AI: Reuse the same placeholder resolver anywhere a standalone fallback
// vessel illustration is needed.
export function PlaceHolderShipImgContainer({
	companyName = "Unspecified",
}: {
	companyName?: string
}): ReactElement {
	return (
		<img
			className={`max-h-100 max-w-4/5 opacity-25`}
			alt="placeholder image vessel"
			src={getVesselPlaceholderImageUrl(companyName)}
		/>
	)
}
